import {
  Box,
  Divider,
  LinearProgress,
  Link,
  Skeleton,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

import { useLanguage } from '../../hooks';
import { useUserDataStore } from '../../newStore/userDataStore';
import { useUserMonthlyActionsQuery } from '../../queries/currentUser';

const customStyles: Record<string, SxProps<Theme>> = {
  root: {
    maxWidth: '240px',
  },
  actionsTitle: {
    fontSize: '14px',
    lineHeight: '22px',
    fontWeight: 500,
    color: '#3B3C40',
  },
  actionsExplanation: {
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: 400,
    color: '#4760F6',
    textDecoration: 'none',
  },
  actionsProgress: {
    margin: '4px 0',
    borderRadius: '8px',
    color: (theme) => theme.palette.primary.main,
    height: '6px',
  },
  actionsCount: {
    fontSize: '14px',
    lineHeight: '22px',
    fontWeight: 500,
    color: '#3B3C40',
  },
  actionsInfoContainer: {
    padding: '3px',
    borderRadius: '8px',
    backgroundColor: '#F0F5FF',
    marginTop: '4px',
  },
  actionsInfo: {
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: 400,
    color: '#3B3C40',
  },
  actionsInfoLink: {
    fontSize: '12px',
    lineHeight: '20px',
    fontWeight: 400,
    color: '#4760F6',
    textDecoration: 'none',
  },
  divider: {
    margin: '4px 0',
    height: '2px',
    borderColor: '#F3F5F9',
  },
  errorContainer: {
    border: '1px solid #FF3F49',
    borderRadius: '8px',
    padding: '2px',
    color: '#DB2E46',
    background: '#FFE4D8',
  },
};

export function UserActionsOverview() {
  const lang = useLanguage();
  const currentUser = useUserDataStore((state) => state.currentUser);
  const { data: userMonthlyActions, isLoading, isError } = useUserMonthlyActionsQuery();

  if (isError) {
    return (
      <Box sx={customStyles.errorContainer}>
        <Typography>{lang.actionsOverview.error}</Typography>
      </Box>
    );
  }

  const isFreeUser = !currentUser?.is_paying;
  const usedActions = userMonthlyActions?.total_actions;
  const userPlanActions = isFreeUser ? 100 : null;
  const actionsProgress = userPlanActions ? (usedActions / userPlanActions) * 100 : 100;
  const actionsProgressText = isFreeUser ? `${usedActions}/${userPlanActions}` : `${usedActions}`;

  return (
    <Box sx={customStyles.root}>
      <Typography sx={customStyles.actionsTitle}>{lang.actionsOverview.title}</Typography>
      <Link
        href={lang.actionsOverview.explanationLink}
        target='_blank'
        sx={customStyles.actionsExplanation}
      >
        {lang.actionsOverview.explanation}
      </Link>
      <LinearProgress
        variant={isLoading ? undefined : 'determinate'}
        value={isLoading ? null : actionsProgress}
        sx={customStyles.actionsProgress}
      />
      {isLoading ? (
        <Skeleton
          variant='text'
          height={22}
        />
      ) : (
        <Typography
          sx={customStyles.actionsTitle}
        >{`${actionsProgressText} ${lang.actionsOverview.actionsUsed}`}</Typography>
      )}
      <Box sx={customStyles.actionsInfoContainer}>
        <Typography sx={customStyles.actionsInfo}>
          {lang.actionsOverview.exceedActionsInfo}
        </Typography>
        <Link
          href={lang.actionsOverview.moreInfoLink}
          target='_blank'
          sx={customStyles.actionsInfoLink}
        >
          {lang.actionsOverview.moreInfo}
        </Link>
      </Box>
      <Divider sx={customStyles.divider} />
    </Box>
  );
}
